/* RESPONSIBLE TEAM: team-reporting */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type AppService from 'embercom/services/app-service';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { Resource } from 'ember-resources';
import { task } from 'ember-concurrency-decorators';
import DataRequestBuilder from 'embercom/lib/reporting/flexible/datarequest-builder';
import ajax from 'embercom/lib/ajax';
import { all, type TaskGenerator } from 'ember-concurrency';
import { debounce } from '@ember/runloop';
import { registerDestructor } from '@ember/destroyable';
import { processRatioPercentageMetricResponses } from 'embercom/lib/reporting/chart-data-resource-compatible-helper';
import ENV from 'embercom/config/environment';
import { convert } from 'embercom/lib/reporting/flexible/table-datarequest-builder';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type ReportingBucketingResponseHandler from 'embercom/services/reporting-bucketing-response-handler';
import type { MetricValue } from 'embercom/lib/reporting/chart-data-resource';
import {
  formatTableValue,
  formatDurationInHours,
} from 'embercom/lib/reporting/custom/view-config-builder-helpers';
import { shouldConvertNullsToZeros } from 'embercom/lib/reporting/flexible/data-response-helpers';
import type { FieldMetric } from 'embercom/objects/reporting/unified/metrics/types';
import type RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import { taskFor } from 'ember-concurrency-ts';

const DEFAULT_URL = '/ember/reporting/signal_flexible';

const ERROR_STATES = [
  'bucket_limit_exceeded',
  'max_regex_filters_exceeded',
  'query_timeout',
  'rate_limit_exceeded',
];

export function getNumericValue(value: MetricValue | null): number | null {
  if (typeof value === 'number') {
    return value;
  }
  return value?.value ?? null;
}

export default class ChartDataResourceCompatible extends Resource {
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare reportingChartService: ReportingChartService;
  @service declare reportingBucketingResponseHandler: ReportingBucketingResponseHandler;
  @service declare reportingMetrics: ReportingMetrics;

  @tracked dataConfig: $TSFixMe = null;
  @tracked renderableChart: RenderableChart;
  @tracked rawChartData: $TSFixMe[] = [];
  onError: ((error: unknown) => void) | null = null;
  onSuccess: (() => void) | null = null;

  constructor(owner: unknown, args: $TSFixMe) {
    super(owner, args);
    let { dataConfig, onError, onSuccess, renderableChart } = (this.args as any).named || this.args;
    this.onError = onError;
    this.onSuccess = onSuccess;
    this.dataConfig = dataConfig;
    this.renderableChart = renderableChart;
    taskFor(this.loadAllData).perform();
    registerDestructor(this, () => {
      taskFor(this.loadAllData).cancelAll();
    });
  }

  @task
  *loadAllData(): TaskGenerator<any> {
    let dataRequests: $TSFixMe[] = [];
    let url: string;
    if (this.renderableChart?.isTable) {
      dataRequests = convert(this.dataConfig, this.renderableChart);
      url = DEFAULT_URL;
    } else {
      let requestBuilder = new DataRequestBuilder({
        dataInputConfiguration: this.dataConfig,
        renderableChart: this.renderableChart,
      });

      dataRequests = requestBuilder.convert();
      url = requestBuilder.dataInputConfiguration.url || DEFAULT_URL;
    }
    let childTasks = dataRequests.map((dataRequest: any) =>
      taskFor(this.fetchData).perform(url, dataRequest),
    );
    try {
      let responses = (yield all(childTasks)) as any[];
      if (this.renderableChart?.isBrokenDownByBucket) {
        let ranges = this.renderableChart.viewByBucketInterval.range;
        this.rawChartData = responses.map((response: any) => {
          return this.reportingBucketingResponseHandler.formatResponse(response, ranges);
        });
      } else {
        this.rawChartData = processRatioPercentageMetricResponses(
          responses,
          this.renderableChart,
          this.renderableChart?.isTable,
        );
      }
      if (this.onSuccess) {
        this.onSuccess();
      }
    } catch (error) {
      if (this.onError) {
        this.onError(error);
      }
      if (this.shouldShowErrorState(error)) {
        return { error: error.jqXHR.responseJSON.error };
      }
      // eslint-disable-next-line no-console
      console.error(error);
      this.notifyError();
    }
  }

  @task
  *fetchData(url: string, dataRequest: $TSFixMe): TaskGenerator<any> {
    return yield ajax({
      url,
      type: 'POST',
      data: JSON.stringify({
        app_id: this.appService.app.id,
        admin_id: this.appService.app.currentAdmin.id,
        ...dataRequest,
        ...(this.reportingChartService.useSyntheticData
          ? { synthetic_data_use_case: this.reportingChartService.syntheticDataUseCase }
          : {}),
      }),
    });
  }

  shouldShowErrorState(error: any) {
    try {
      return ERROR_STATES.includes(error.jqXHR?.responseJSON?.error);
    } catch {
      return false;
    }
  }

  notifyError() {
    debounce(
      this.notificationsService,
      this.notificationsService.notifyError,
      'Something went wrong, try reloading the page.',
      ENV.APP._1000MS,
      ENV.APP._1000MS,
      true,
    );
  }

  get error(): any {
    return taskFor(this.loadAllData).last?.value?.error;
  }

  get isLoading() {
    return taskFor(this.loadAllData).isRunning;
  }

  formatValue(options: {
    value: MetricValue;
    metricId?: string;
    aggregation?: string;
    durationFormat?: 'hours';
  }): string {
    let { value, metricId, aggregation, durationFormat } = options;
    let metric = metricId
      ? this.reportingMetrics.getMetricById(metricId)
      : this.renderableChart.firstMetric;
    let numericValue = getNumericValue(value);
    let formattedValue;
    if (metric.unit === 'seconds' && durationFormat === 'hours') {
      formattedValue = formatDurationInHours(numericValue, metric.unit);
    } else {
      formattedValue = formatTableValue(numericValue, metric.unit);
    }

    aggregation = aggregation || (metric as FieldMetric).defaultAggregation;
    let defaultValue = shouldConvertNullsToZeros(aggregation, metric) ? 0 : '-';
    return formattedValue || defaultValue;
  }
}
